import { useCallback } from 'react';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';
import DocsIcon from '@material-ui/icons/Description';
import { HeaderIconLinkRow, Link } from '@backstage/core-components';
import { alertApiRef, errorApiRef, useApi } from '@backstage/core-plugin-api';
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { AboutContent } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  EntityInfoCard,
  getEntitySourceLocation,
  useEntity,
} from '@backstage/plugin-catalog-react';
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import { findDocsLink } from './findDocsLink';

const useStyles = makeStyles({
  linkContainer: {
    border: '1px solid var(--bui-border-1)',
    borderLeft: 'none',
    borderRight: 'none',
    marginBottom: 'var(--bui-space-6)',
  },
});

// Same as the upstream About card, with the TechDocs button replaced by a Docs
// button that opens the entity's Confluence link.
export const ItgAboutCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);

  const sourceLocation = getEntitySourceLocation(entity, scmIntegrationsApi);
  const docsLink = findDocsLink(entity);
  const editUrl = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
  const location = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    location?.startsWith('url:') || location?.startsWith('file:');

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (e) {
      errorApi.post(e as Error);
    }
  }, [catalogApi, entity, alertApi, errorApi]);

  const links = [
    {
      label: 'View Source',
      disabled: !sourceLocation,
      icon: <ScmIntegrationIcon type={sourceLocation?.integrationType} />,
      href: sourceLocation?.locationTargetUrl,
    },
    {
      label: 'Docs',
      title: docsLink?.title,
      disabled: !docsLink,
      icon: <DocsIcon />,
      href: docsLink?.url,
    },
  ];

  return (
    <EntityInfoCard
      title="About"
      headerActions={
        <>
          {allowRefresh && (
            <IconButton
              aria-label="Refresh"
              title="Schedule entity refresh"
              onClick={refreshEntity}
            >
              <CachedIcon />
            </IconButton>
          )}
          <IconButton
            component={Link}
            aria-label="Edit"
            disabled={!editUrl}
            title="Edit Metadata"
            to={editUrl ?? '#'}
          >
            <EditIcon />
          </IconButton>
        </>
      }
    >
      <div className={classes.linkContainer}>
        <HeaderIconLinkRow links={links} />
      </div>
      <AboutContent entity={entity} />
    </EntityInfoCard>
  );
};
